import React, { useEffect, useRef } from 'react';
import { Box, Button, Heading, Text } from '@wix/design-system';
import { Player } from '@lottiefiles/react-lottie-player';
import { StatusCompleteFilled } from '@wix/wix-ui-icons-common';
import { useTranslation } from '@wix/wix-i18n-config';
import animationData from 'assets/lotties/success-confetti.json';
import { useIsMobile } from 'hooks/useIsMobile';
import {
  EXISTING_SITE_STORE_READY_TASK_DEFS,
  getExistingSiteStoreReadyTaskDescriptionKey,
  getExistingSiteStoreReadyTaskTitleKey,
} from './existingSiteStoreReadyTasks';
import { useExistingSiteStoreReadyUiStore, type ExistingSiteStoreReadyBoxId } from './existingSiteStoreReadyUiStore';
import { ExistingSiteStoreReadyTaskCard } from './components/ExistingSiteStoreReadyTaskCard';
import { ExistingSiteStoreReadyDetailPanel } from './components/ExistingSiteStoreReadyDetailPanel';
import { TextDivider } from 'components/TextDivider/TextDivider';
import { WIX_EDITOR_URL } from 'config/consts';
import { biEvents } from 'api/bi-events';
import { BI_BUTTON_NAME, BI_STEP_NAME } from 'config/bi';
import { StepId } from 'config/steps';
import { actions } from 'api/actions';
import { useAppStore } from 'stores/appStore';
import { getPublishPathLabel } from './existingSiteStoreReadyUtils';
import styles from './ExistingSiteStoreReadyStep.scss';

export function ExistingSiteStoreReadyMain() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const selectedBoxId = useExistingSiteStoreReadyUiStore((s) => s.selectedBoxId);
  const mobileExpandedBoxId = useExistingSiteStoreReadyUiStore((s) => s.mobileExpandedBoxId);
  const setSelectedBoxId = useExistingSiteStoreReadyUiStore((s) => s.setSelectedBoxId);
  const setMobileExpandedBoxId = useExistingSiteStoreReadyUiStore((s) => s.setMobileExpandedBoxId);
  const resetToDefaults = useExistingSiteStoreReadyUiStore((s) => s.resetToDefaults);
  const siteUrl = useAppStore((s) => s.siteUrl);
  const metaSiteId = useAppStore((s) => s.metaSiteId);
  const editorHref = metaSiteId ? `${WIX_EDITOR_URL}/${metaSiteId}` : undefined;
  const publishPathLabel = getPublishPathLabel(siteUrl, t('existing-site-store-ready.path-badge.fallback-label'));
  const cardRefs = useRef<Partial<Record<ExistingSiteStoreReadyBoxId, HTMLElement | null>>>({});

  const tasks = EXISTING_SITE_STORE_READY_TASK_DEFS.map((def) => ({
    id: def.id,
    icon: def.icon,
    title: t(getExistingSiteStoreReadyTaskTitleKey(def.id)),
    description: t(getExistingSiteStoreReadyTaskDescriptionKey(def.id)),
  }));

  useEffect(() => {
    resetToDefaults();
  }, [resetToDefaults]);

  // Ensure the store URL is available for the publish label, even if the page is
  // reached without the store-setup tasks having populated it.
  useEffect(() => {
    if (!siteUrl && metaSiteId) {
      void actions.fetchSiteUrl();
    }
  }, [siteUrl, metaSiteId]);

  useEffect(() => {
    setMobileExpandedBoxId(isMobile ? EXISTING_SITE_STORE_READY_TASK_DEFS[0].id : null);
  }, [isMobile, setMobileExpandedBoxId]);

  useEffect(() => {
    if (!isMobile || !mobileExpandedBoxId) {
      return undefined;
    }

    const node = cardRefs.current[mobileExpandedBoxId];
    if (!node) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isMobile, mobileExpandedBoxId]);

  function logEditorClick() {
    biEvents.actionClicked({
      screenName: BI_STEP_NAME[StepId.StoreReady],
      button_name: BI_BUTTON_NAME.DESIGN_SITE,
    });
  }

  function handleSelectTask(id: ExistingSiteStoreReadyBoxId) {
    setSelectedBoxId(id);

    if (isMobile) {
      const current = useExistingSiteStoreReadyUiStore.getState().mobileExpandedBoxId;
      setMobileExpandedBoxId(current === id ? null : id);
    }
  }

  function handleCloseDrawer() {
    setMobileExpandedBoxId(null);
  }

  return (
    <div className={styles.existingSiteStoreReady}>
      <div className={styles.existingSiteStoreReadyInner}>
        <Box direction="vertical" align="center" gap="SP4" width="100%">
          <section aria-labelledby="existing-site-store-ready-title">
            <Box direction="vertical" align="center" gap="SP3" textAlign="center">
              <div className={styles.existingSiteStoreReadyConfetti}>
                <Player loop autoplay className={styles.existingSiteStoreReadyConfettiLottie} src={animationData} />
                <StatusCompleteFilled className={styles.existingSiteStoreReadyConfettiIcon} aria-hidden />
              </div>
              <Box direction="vertical" align="center" gap="SP1" textAlign="center">
                <Heading as="h1" size="extraLarge" id="existing-site-store-ready-title">
                  {t('existing-site-store-ready.hero.title')}
                </Heading>
                <div className={styles.existingSiteStoreReadyHeroSubtitle}>
                  <Text secondary>{t('existing-site-store-ready.hero.subtitle')}</Text>
                </div>
              </Box>
            </Box>
          </section>

          {isMobile && editorHref && (
            <Button
              as="a"
              href={editorHref}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              priority="secondary"
              onClick={logEditorClick}
            >
              {publishPathLabel}
            </Button>
          )}

          <section aria-label={t('existing-site-store-ready.hero.before-publish-section-aria')}>
            <Box direction="vertical" gap="SP2" width="100%">
              {tasks.map((task, index) => {
                const isSelected = selectedBoxId === task.id;
                const isDrawerOpen = isMobile && mobileExpandedBoxId === task.id;

                return (
                  <React.Fragment key={task.id}>
                    {index === 1 && (
                      <div className={styles.existingSiteStoreReadyTextDivider}>
                        <TextDivider text={t('store-ready-step.content.divider')} />
                      </div>
                    )}
                    <ExistingSiteStoreReadyTaskCard
                      task={task}
                      isSelected={isSelected}
                      isMobileDrawerOpen={Boolean(isDrawerOpen)}
                      cardRef={(node) => {
                        cardRefs.current[task.id] = node;
                      }}
                      inlinePanel={
                        isMobile && isDrawerOpen ? <ExistingSiteStoreReadyDetailPanel boxId={task.id} /> : undefined
                      }
                      cta={
                        task.id === 'page-ready' && editorHref && !isMobile ? (
                          <Button
                            as="a"
                            href={editorHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            priority="primary"
                            onClick={logEditorClick}
                          >
                            {t('existing-site-store-ready.tasks.page-ready.cta')}
                          </Button>
                        ) : undefined
                      }
                      onSelect={() => handleSelectTask(task.id)}
                      onCloseMobileDrawer={handleCloseDrawer}
                    />
                  </React.Fragment>
                );
              })}
            </Box>
          </section>
        </Box>
      </div>
    </div>
  );
}
